// ─────────────────────────────────────────────────────────────
// QuestionColumn — left rail: searchable list of linkable questions.
// Each row shows the question, MetaKey chip, link count, and a row
// of draggable answer pills (Yes/No, code options, or "value").
// ─────────────────────────────────────────────────────────────

import * as React from 'react';
import { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, TextField, InputAdornment, Card, CardActionArea,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';

import type { SDM, LinkDef, OID, ItemDef, CodeList, DragSource, TriggerValue } from '../types';
import { linksFor } from '../expressions';
import { MetaKeyChip, TypeIcon, LinkCount } from '../Bits';

export interface QuestionColumnProps {
  sdm: SDM;
  links: LinkDef[];
  selectedOID: OID | null;
  hoveredTaskId: number | null;
  onSelect: (oid: OID | null) => void;
  onStartDrag: (p: DragSource) => void;
  onEndDrag: () => void;
}

const LINKABLE_TYPES = new Set(['integer', 'float', 'boolean', 'singleselect', 'multiselect']);

export function QuestionColumn({
  sdm, links, selectedOID, hoveredTaskId, onSelect, onStartDrag, onEndDrag,
}: QuestionColumnProps): JSX.Element {
  const tv = sdm.TemplateVersion[0];
  const codeByOid = useMemo(
    () => Object.fromEntries(tv.CodeList.map(c => [c.OID, c])) as Record<OID, CodeList>,
    [tv.CodeList],
  );

  const [filter, setFilter] = useState('');

  const linkable = useMemo(
    () => tv.ItemDef.filter(it => LINKABLE_TYPES.has(it.DataType)),
    [tv.ItemDef],
  );

  const filtered = useMemo(() => {
    if (!filter) return linkable;
    const f = filter.toLowerCase();
    return linkable.filter(it =>
      (it.Question?.TranslatedText?.text ?? '').toLowerCase().includes(f) ||
      it.OID.toLowerCase().includes(f) ||
      (it.MetaKey ?? '').toLowerCase().includes(f),
    );
  }, [linkable, filter]);

  const linkedItemOIDs = useMemo(() => hoveredTaskId
    ? new Set(linksFor(links, { taskId: hoveredTaskId }).map(l => l.sourceItemOID))
    : null,
  [hoveredTaskId, links]);

  return (
    <Box sx={{
      height: '100%', overflow: 'auto', bgcolor: 'background.paper',
      borderRight: 1, borderColor: 'divider',
      display: 'flex', flexDirection: 'column',
    }}>
      <Box sx={{
        p: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider',
        position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 2,
      }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
          Questions &middot; {filtered.length}
        </Typography>
        <TextField
          size="small" fullWidth
          placeholder="Search questions, MetaKey..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>,
          }}
        />
      </Box>

      <Box sx={{ p: 1, pb: 10 }}>
        {filtered.map(item => {
          const itemLinks = linksFor(links, { itemOID: item.OID });
          const sel = selectedOID === item.OID;
          const dim = linkedItemOIDs ? !linkedItemOIDs.has(item.OID) : false;
          const codeList = item.CodeListRef ? codeByOid[item.CodeListRef.CodeListOID] : null;
          return (
            <Card
              key={item.OID}
              variant="outlined"
              sx={{
                mb: 0.5,
                borderColor: sel ? 'primary.main' : 'divider',
                bgcolor: sel ? 'primary.50' : 'background.paper',
                opacity: dim ? 0.4 : 1,
                transition: 'all .12s',
              }}
            >
              <CardActionArea onClick={() => onSelect(item.OID === selectedOID ? null : item.OID)}>
                <Box sx={{ p: 1.25 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                    <TypeIcon type={item.DataType} />
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, lineHeight: 1.3 }}>
                      {item.Question.TranslatedText.text}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'text.disabled' }}>
                      {item.OID}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
                    <MetaKeyChip value={item.MetaKey} />
                    <LinkCount count={itemLinks.length} />
                  </Stack>
                  <AnswerPills
                    item={item}
                    codeList={codeList}
                    onStartDrag={onStartDrag}
                    onEndDrag={onEndDrag}
                  />
                </Box>
              </CardActionArea>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.disabled', p: 3 }}>
            No linkable questions match.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── AnswerPills ──────────────────────────────────────────────

interface PillSpec {
  label: string;
  value: TriggerValue | null;
  color: string;
  generic?: boolean;
}

interface AnswerPillsProps {
  item: ItemDef;
  codeList: CodeList | null;
  onStartDrag: (p: DragSource) => void;
  onEndDrag: () => void;
}

function AnswerPills({ item, codeList, onStartDrag, onEndDrag }: AnswerPillsProps): JSX.Element {
  const pills: PillSpec[] = (() => {
    if (item.DataType === 'boolean') {
      return [
        { label: 'Yes', value: true,  color: 'success.main' },
        { label: 'No',  value: false, color: 'error.main' },
      ];
    }
    if (item.DataType === 'singleselect' || item.DataType === 'multiselect') {
      return (codeList?.CodeListItem ?? []).map(ci => ({
        label: ci.Decode.TranslatedText.text,
        value: ci.CodedValue,
        color: 'info.main',
      }));
    }
    return [{ label: 'value', value: null, color: 'success.main', generic: true }];
  })();

  const startDrag = (pill: PillSpec, e: React.DragEvent) => {
    const payload: DragSource = {
      itemOID: item.OID,
      value: pill.value,
      generic: !!pill.generic,
    };
    try { e.dataTransfer.setData('application/x-naomi-link', JSON.stringify(payload)); } catch { /* noop */ }
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'link';
    onStartDrag(payload);
  };

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
      {pills.map((p, i) => (
        <Box
          key={i}
          draggable
          onDragStart={e => startDrag(p, e)}
          onDragEnd={onEndDrag}
          title={p.generic ? 'Drag to a task to link the value' : `Drag to a task to fire when "${p.label}"`}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: 1, py: 0.25, borderRadius: 0.75,
            cursor: 'grab', userSelect: 'none',
            bgcolor: 'action.hover',
            color: 'text.primary',
            border: 1, borderColor: 'divider',
            fontSize: 11.5, fontWeight: 500,
            transition: 'all .12s',
            '&:hover': { borderColor: p.color, color: p.color },
          }}
        >
          <LinkIcon sx={{ fontSize: 12 }} />
          {p.label}
        </Box>
      ))}
    </Stack>
  );
}

